import { CrescLoader } from "./LoadingScreen";
import { useState, useEffect, useCallback } from "react";
import { Activity, CheckCircle, PlusCircle, UserCheck, MessageSquare, FileEdit, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { formatDistanceToNow } from "date-fns";

const ICONS = {
  node_created: PlusCircle,
  node_completed: CheckCircle,
  node_assigned: UserCheck,
  comment_added: MessageSquare,
  doc_edited: FileEdit,
};
const COLORS = {
  node_created: "#5A90F5",
  node_completed: "#22C55E",
  node_assigned: "#F59E0B",
  comment_added: "#8B5CF6",
  doc_edited: "#A1A1AA",
};

export default function ActivityFeed() {
  const workspaceCtx = useWorkspace();
  const current = workspaceCtx?.current;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!current?.workspace_id) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/workspaces/${current.workspace_id}/activity?limit=50`);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Could not load activity.");
    } finally {
      setLoading(false);
    }
  }, [current?.workspace_id]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 30s
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-[#2A2A2A] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#5A90F5]" />
          <span className="text-sm font-semibold text-[#EDEDED]">Activity</span>
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#EDEDED]" title="Refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {loading && entries.length === 0 && (
          <div className="text-center py-8">
            <RefreshCw className="w-5 h-5 text-[#3A3A3A] mx-auto mb-2 animate-spin" />
          </div>
        )}
        {error && (
          <div className="text-center py-8">
            <p className="text-[11px] text-[#52525B]">{error}</p>
          </div>
        )}
        {entries.length === 0 && !loading && !error && (
          <div className="text-center py-8">
            <Activity className="w-8 h-8 text-[#3A3A3A] mx-auto mb-2" />
            <p className="text-[11px] text-[#52525B]">No activity yet.</p>
          </div>
        )}
        {entries.map(entry => {
          const Icon = ICONS[entry.event_type] || Activity;
          const color = COLORS[entry.event_type] || "#A1A1AA";
          const timeAgo = (() => {
            try { return formatDistanceToNow(new Date(entry.created_at), { addSuffix: true }); } catch { return ""; }
          })();
          return (
            <div key={entry.activity_id} className="flex gap-2.5 py-2 border-b border-[#1a1a1a]">
              <div className="flex-shrink-0 mt-0.5">
                {entry.actor_picture
                  ? <img src={entry.actor_picture} alt="" className="w-6 h-6 rounded-full" />
                  : <div className="w-6 h-6 rounded-full bg-[#1a1a1a] flex items-center justify-center"><Icon className="w-3 h-3" style={{ color }} /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] text-[#EDEDED] leading-snug">
                    {entry.actor_name && <span className="font-medium">{entry.actor_name} </span>}
                    {entry.message || entry.event_type.replace(/_/g, " ")}
                    {entry.entity_title && <span className="text-[#A1A1AA]"> — <em className="not-italic font-medium">{entry.entity_title}</em></span>}
                  </p>
                  <Icon className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color }} />
                </div>
                <p className="text-[9px] text-[#52525B] mt-0.5">{timeAgo}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
